import { useTranslation } from 'react-i18next';
import { AlertTriangle, ExternalLink, MoreHorizontal, Pencil, Power, Presentation, Trash2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { LogoMark, StatusBadge, ValueWithChange, formatNumber } from '../../../../components/design-system/admin';
import type { OverviewVendorRow } from '../../../../common/models/admin-overview.model';
import { buildVisualizerUrl, PREVIEW_LINK_ATTRIBUTION } from '../../../../common/utils/visualizer-link';
import { Vendor } from '../models/vendor.model';

/**
 * The last-30-days numbers for each vendor, from the admin overview, joined to
 * the list by id. `failed` means the overview could not be read: the list still
 * renders, with '—' where those numbers go.
 */
export interface VendorUsage {
    state: 'loading' | 'ready' | 'failed';
    byId: Map<string, OverviewVendorRow>;
}

export const usageOf = (usage: VendorUsage, vendor: Vendor): OverviewVendorRow | undefined =>
    usage.state === 'ready' && vendor._id ? usage.byId.get(String(vendor._id)) : undefined;

/** What the list and the grid share: the page's rows, handlers and footer. */
export interface VendorCollectionProps {
    vendors: Vendor[];
    usage: VendorUsage;
    pagination: { page: number; limit: number; total: number; totalPages: number };
    loading: boolean;
    handleEditVendor: (vendor: Vendor) => void;
    onDelete: (vendor: Vendor) => void;
    onToggleStatus: (vendor: Vendor) => void;
    /** A vendor that is really a demo moves to the prospects. */
    onMoveToProspects?: (vendor: Vendor) => void;
    /** Zero-based. */
    onPageChange: (page: number) => void;
    onRowsPerPageChange: (limit: number) => void;
}

export const ROWS_PER_PAGE_OPTIONS = [10, 20, 50];

/** Opens the vendor's visualizer in a new tab, tagged as a preview so it stays out of their analytics. */
export const openVendorVisualizer = (vendor: Vendor) => {
    const visualizerUrl = buildVisualizerUrl({ alias: vendor.alias, attribution: PREVIEW_LINK_ATTRIBUTION });
    window.open(visualizerUrl, '_blank');
};

export const isVendorEnabled = (vendor: Vendor) => vendor.isEnabled !== false;

/** The vendor's logo, or its initials when there is none (or it fails to load). */
export function VendorLogo({ vendor, className }: { vendor: Vendor; className?: string }) {
    const { t } = useTranslation();
    return (
        <LogoMark
            src={vendor.config?.logo}
            name={vendor.name}
            alt={t('ADMIN_VENDORS.LOGO_ALT', { name: vendor.name })}
            className={className}
        />
    );
}

export function VendorStatus({ vendor }: { vendor: Vendor }) {
    const { t } = useTranslation();
    return isVendorEnabled(vendor) ? (
        <StatusBadge tone="success">{t('ADMIN_SHELL.COMMON.ENABLED')}</StatusBadge>
    ) : (
        <StatusBadge tone="danger">{t('ADMIN_SHELL.COMMON.DISABLED')}</StatusBadge>
    );
}

/** A value the overview supplies: a bar while it loads, then the value (which says '—' itself when missing). */
export function UsageValue({ usage, end, children }: { usage: VendorUsage; end?: boolean; children: React.ReactNode }) {
    if (usage.state === 'loading') return <Skeleton className={cn('h-3.5 w-10', end && 'ms-auto')} />;
    return <>{children}</>;
}

/** Visualizations in the period, and the change against the period before. */
export function VisualizationsWithChange({ row, className }: { row?: OverviewVendorRow; className?: string }) {
    const { t } = useTranslation();
    if (!row) return <span className={className}>—</span>;
    return (
        <ValueWithChange
            value={row.visualizations}
            previous={row.visualizationsPrevious}
            title={t('ADMIN_SHELL.COMMON.VS_PREVIOUS', { days: 30 })}
            className={className}
        />
    );
}

/**
 * Products (V2, from the overview) against the vendor's own limit when it has
 * one. The legacy `vendor.products` array is not counted: V2 never updates it.
 */
export function ProductsCount({ vendor, row }: { vendor: Vendor; row?: OverviewVendorRow }) {
    const { t } = useTranslation();
    if (!row) return <span>—</span>;
    const count = row.products;
    const max = typeof vendor.maxProducts === 'number' && vendor.maxProducts > 0 ? vendor.maxProducts : null;
    if (max === null) return <span className="tabular-nums">{formatNumber(count)}</span>;

    const over = count > max;
    const atLimit = count === max;
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <span
                    tabIndex={0}
                    className={cn(
                        'inline-flex items-center justify-end gap-1 tabular-nums',
                        over ? 'text-destructive' : atLimit ? 'text-warning' : undefined,
                    )}
                >
                    {over && <AlertTriangle className="size-3.5" />}
                    {formatNumber(count)}
                    <span className={cn(!over && !atLimit && 'text-muted-foreground')}>/ {formatNumber(max)}</span>
                </span>
            </TooltipTrigger>
            <TooltipContent>
                {over
                    ? t('ADMIN_VENDORS.LIST.PRODUCTS_OVER', { count, max })
                    : t('ADMIN_VENDORS.LIST.PRODUCTS_OF', { count, max })}
            </TooltipContent>
        </Tooltip>
    );
}

/** The row's ⋯ menu: edit, open the visualizer, enable/disable, delete. */
export function VendorActionsMenu({
    vendor,
    onEdit,
    onToggleStatus,
    onDelete,
    onMoveToProspects,
}: {
    vendor: Vendor;
    onEdit: (vendor: Vendor) => void;
    onToggleStatus: (vendor: Vendor) => void;
    onDelete: (vendor: Vendor) => void;
    onMoveToProspects?: (vendor: Vendor) => void;
}) {
    const { t } = useTranslation();
    const enabled = isVendorEnabled(vendor);
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t('ADMIN_VENDORS.MENU.ACTIONS_FOR', { name: vendor.name })}
                    onClick={(e) => e.stopPropagation()}
                >
                    <MoreHorizontal className="size-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-44">
                <DropdownMenuItem onClick={() => onEdit(vendor)}>
                    <Pencil className="size-4" />
                    {t('ADMIN_VENDORS.MENU.EDIT')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => openVendorVisualizer(vendor)}>
                    <ExternalLink className="size-4" />
                    {t('ADMIN_SHELL.COMMON.OPEN_VISUALIZER')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onToggleStatus(vendor)}>
                    <Power className="size-4" />
                    {enabled ? t('ADMIN_VENDORS.MENU.DISABLE') : t('ADMIN_VENDORS.MENU.ENABLE')}
                </DropdownMenuItem>
                {onMoveToProspects && (
                    <DropdownMenuItem onClick={() => onMoveToProspects(vendor)}>
                        <Presentation className="size-4" />
                        {t('ADMIN_VENDORS.MENU.MOVE_TO_PROSPECTS')}
                    </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive" onClick={() => onDelete(vendor)}>
                    <Trash2 className="size-4" />
                    {t('ADMIN_VENDORS.MENU.DELETE')}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
